export interface ScormBridgeCallbacks {
  // An embedded PDF/attachment was opened; `url` is the resolved same-origin URL.
  onPdf: (url: string, fileName: string) => void;
  // An embedded video was expanded (bridge already paused it); `currentTime` (s)
  // is where it was so the host overlay can start there.
  onVideo: (src: string, currentTime: number) => void;
}

const BRIDGE_NAMESPACE = 'scorm-bridge';

// Host-side client for the package's injected postMessage bridge: consumes the
// embedded-asset handoffs (attachment-click, video-expand), sends back commands.
export class ScormBridge {
  private readonly messageHandler = (e: MessageEvent) => this.onMessage(e);

  constructor(
    private readonly iframe: HTMLIFrameElement,
    private readonly callbacks: ScormBridgeCallbacks
  ) {}

  attach(): void {
    // addEventListener dedups identical handlers, so repeated attach() is safe.
    window.addEventListener('message', this.messageHandler);
  }

  detach(): void {
    window.removeEventListener('message', this.messageHandler);
  }

  /** Pause every `<video>` inside the package (e.g. when opening a PDF overlay). */
  pauseVideos(): void {
    this.postCommand('pause-videos');
  }

  // Release a handed-off video and re-sync it to where the host overlay left off.
  seekVideo(src: string, currentTime: number): void {
    this.postCommand('seek-video', { src, currentTime });
  }

  private postCommand(command: string, args: Record<string, unknown> = {}): void {
    this.iframe.contentWindow?.postMessage(
      { namespace: BRIDGE_NAMESPACE, command, ...args },
      window.location.origin
    );
  }

  private onMessage(event: MessageEvent): void {
    if (event.origin && event.origin !== window.location.origin) return;
    const data = event.data;
    if (!data || data.namespace !== BRIDGE_NAMESPACE || !data.type) return;

    switch (data.type) {
      case 'attachment-click': {
        const url = data.payload?.url ? String(data.payload.url) : '';
        if (!url) return;
        // PDF: the injected bridge supplies the file name; the host correlates
        // the child by url (s3Location) first, file name as fallback.
        const fileName = data.payload?.filename ? String(data.payload.filename) : '';
        this.callbacks.onPdf(url, fileName);
        break;
      }
      case 'video-expand': {
        const src = data.payload?.src ? String(data.payload.src) : '';
        if (!src) return;
        // Video: the bridge carries no file name — the host correlates the child
        // by exact src === child.s3Location match.
        const currentTime = Number(data.payload?.currentTime) || 0;
        this.callbacks.onVideo(src, currentTime);
        break;
      }
    }
  }
}
